import * as O from 'fp-ts/Option';
import { Bundling } from '../../../data/bundling';
import { FurnitureItem } from '../../../data/furniture';
import * as ProductSetLayout from '../../../data/productSetLayout';
import * as Selectors from '../../../store/selectors';
import * as Arya from '../../arya/interface';
import { useSelector } from '../../hooks/redux';
import { useAddCommand } from '../../hooks/useConsoleCommand';

export const useCommands = (
    aryaRef: O.Option<Arya.Interface>,
    itemBundling: Bundling,
    furnitureList: FurnitureItem[],
) => {
    useView2DCommand(aryaRef);
    useView3DCommand(aryaRef);
    useFocusOnSelectedFurnitureCommand(aryaRef);
    useDownloadGlbCommand(aryaRef, itemBundling);
    useExportLayoutCommand(aryaRef, itemBundling, furnitureList);
};

export const useView2DCommand = (aryaRef: O.Option<Arya.Interface>) => {
    useAddCommand(
        'view2d',
        () => {
            O.toNullable(aryaRef)?.toggleViewMode('2D');
        },
        [aryaRef],
    );
};

export const useView3DCommand = (aryaRef: O.Option<Arya.Interface>) => {
    useAddCommand(
        'view3d',
        () => {
            O.toNullable(aryaRef)?.toggleViewMode('3D');
        },
        [aryaRef],
    );
};

/**
 * Focus the camera on the selected furniture, making the furniture the center of the camera.
 * This is useful for inspecting a model.
 * To return to normal, call the `view3d` command.
 */
export const useFocusOnSelectedFurnitureCommand = (aryaRef: O.Option<Arya.Interface>) => {
    useAddCommand(
        'focusOnSelectedFurniture',
        () => {
            const a = O.toNullable(aryaRef);

            const sel = a?.selectedFurniture()[0];

            if (sel !== undefined) {
                console.log('to return to normal, call the view3d command');
                a?.focusOnFurniture(sel);
                O.toNullable(aryaRef)?.toggleViewMode('Focus');
            }
        },
        [aryaRef],
    );
};

export const useDownloadGlbCommand = (
    aryaRef: O.Option<Arya.Interface>,
    itemBundling: Bundling,
) => {
    useAddCommand(
        'downloadGlb',
        () => {
            const a = O.toNullable(aryaRef);

            const res = a?.generateFurnitureGLTF();

            if (res) {
                res.then(gltf => {
                    const blob = new Blob([gltf], { type: 'application/octet-binary' });
                    const link = document.createElement('a');

                    link.href = URL.createObjectURL(blob);
                    link.download = `${itemBundling.bundles[0]?.canonicalId ?? 'export'}.glb`;

                    link.click();
                });
            } else {
                console.error('error generating glb');
            }
        },
        [aryaRef, itemBundling],
    );
};

export const useExportLayoutCommand = (
    aryaRef: O.Option<Arya.Interface>,
    itemBundling: Bundling,
    furnitureList: FurnitureItem[],
) => {
    const currentGlobalConfigurablePropertySelections = useSelector(
        Selectors.ConfigurableProperties.currentPropertySelections,
    );

    useAddCommand(
        'exportLayout',
        () => {
            const setLayout = ProductSetLayout.generate(
                aryaRef,
                furnitureList,
                itemBundling.bundles[0]?.canonicalId,
                currentGlobalConfigurablePropertySelections,
            );
            // not using the logging system to avoid it being turned off at the wrong log level
            console.info(JSON.stringify(setLayout, null, 2));
            // Cannot perform clipboard operation via console command for security reasons
            // (dom not focused exception)
            console.log(
                'Note: cannot copy layout to clipboard for security reasons - please use button in scene',
            );
        },
        [aryaRef, furnitureList, itemBundling, currentGlobalConfigurablePropertySelections],
    );
};
